import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Save, X, Type, Highlighter,
  MessageSquare, Pencil, Underline, Strikethrough, Undo, Redo, MousePointer,
  Move, Palette, Loader2, FolderOpen, Printer, Image as ImageIcon
} from "lucide-react";

const PDFEditor = ({ document, onClose, onSave, onSwitchToCanvasEditor }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageImage, setPageImage] = useState(null);
  const [imageKey, setImageKey] = useState(0); // Force image re-render after save
  const [zoom, setZoom] = useState(100);
  const [activeTool, setActiveTool] = useState("select"); // select, text, highlight, underline, strikethrough, comment, draw
  const [highlightColor, setHighlightColor] = useState("#FFFF00");
  
  // Edit states
  const [textEdits, setTextEdits] = useState([]);
  const [textAdditions, setTextAdditions] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [annotations, setAnnotations] = useState([]);
  
  // UI states
  const [selectedText, setSelectedText] = useState(null);
  const [editingText, setEditingText] = useState(null);
  const [textOffset, setTextOffset] = useState({ x: 0, y: 0 });  // Position adjustment for edited text
  const [detectedBgColor, setDetectedBgColor] = useState(null);
  const [detectingColor, setDetectingColor] = useState(false);
  const [detectedFont, setDetectedFont] = useState(null); // Detected font properties from original text
  const [fontSuggestion, setFontSuggestion] = useState(null); // AI suggested font family + candidates
  const [availableFonts, setAvailableFonts] = useState([]); // Curated font catalogue from backend
  // Hover-preview: maps block.id -> { color, samples } so we can show the
  // detected paper colour as a small badge before user clicks. Avoids
  // surprises where the cover ends up a different colour than expected.
  const [hoverPaperColors, setHoverPaperColors] = useState({});
  const [hoveredBlock, setHoveredBlock] = useState(null);
  const hoverTimerRef = useRef(null);
  // Ref for the inline editable input that appears on top of the selected text block
  const inlineEditRef = useRef(null);
  const [flattenOnSave, setFlattenOnSave] = useState(false); // Redaction-proof PDF
  const [showMobilePropsPanel, setShowMobilePropsPanel] = useState(false);
  const [fontSettings, setFontSettings] = useState({ 
    size: 12, 
    family: 'helv', 
    bold: false, 
    italic: false,
    color: '#000000',
    underline: false,
    strikethrough: false
  });
  const [isDrawing, setIsDrawing] = useState(false);
  const [drawingPoints, setDrawingPoints] = useState([]);
  const [addTextDialog, setAddTextDialog] = useState(false);
  const [newTextPosition, setNewTextPosition] = useState({ x: 0, y: 0 });
  const [newText, setNewText] = useState("");
  const [newTextSize, setNewTextSize] = useState(12);
  const [commentDialog, setCommentDialog] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentPosition, setCommentPosition] = useState({ x: 0, y: 0 });
  const [saveToLibraryDialog, setSaveToLibraryDialog] = useState(false);
  
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  // Touch-gesture state for pinch-zoom + two-finger pan
  const pinchStateRef = useRef(null);

  const handleTouchStart = (e) => {
    if (e.touches.length === 2) {
      const t1 = e.touches[0]; const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const cx = (t1.clientX + t2.clientX) / 2;
      const cy = (t1.clientY + t2.clientY) / 2;
      const sc = containerRef.current;
      pinchStateRef.current = {
        startDist: dist, startZoom: zoom,
        startCx: cx, startCy: cy,
        startScrollLeft: sc ? sc.scrollLeft : 0,
        startScrollTop: sc ? sc.scrollTop : 0,
      };
      e.preventDefault();
    }
  };
  const handleTouchMove = (e) => {
    if (e.touches.length === 2 && pinchStateRef.current) {
      const t1 = e.touches[0]; const t2 = e.touches[1];
      const dist = Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY);
      const cx = (t1.clientX + t2.clientX) / 2;
      const cy = (t1.clientY + t2.clientY) / 2;
      const ps = pinchStateRef.current;
      const newZoom = Math.max(50, Math.min(400, Math.round(ps.startZoom * (dist / ps.startDist))));
      if (newZoom !== zoom) setZoom(newZoom);
      const sc = containerRef.current;
      if (sc) {
        sc.scrollLeft = ps.startScrollLeft - (cx - ps.startCx);
        sc.scrollTop = ps.startScrollTop - (cy - ps.startCy);
      }
      e.preventDefault();
    }
  };
  const handleTouchEnd = (e) => {
    if (e.touches.length < 2) pinchStateRef.current = null;
  };
  
  // Selection for highlight/underline
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionRect, setSelectionRect] = useState(null);

  useEffect(() => {
    const fetchPdfData = async () => {
      try {
        const response = await axios.get(
          `${API}/documents/${document.id}/pdf-pages`,
          { headers: getAuthHeader(), timeout: 30000 }
        );
        setPdfData(response.data);
        setLoading(false);
        
        if (response.data.is_ocr_document) {
          toast.info("This is an image-based PDF. Text has been extracted using OCR for editing.", {
            duration: 5000
          });
        }
      } catch (error) {
        console.error("PDFEditor fetch error:", error.message);
        toast.error("Failed to load PDF");
        setLoading(false);
      }
    };
    // Fetch the curated font catalogue once
    axios.get(`${API}/pdf-editor/fonts`, { headers: getAuthHeader() })
      .then(r => setAvailableFonts(r.data?.fonts || []))
      .catch(() => {});
    
    fetchPdfData();
  }, [document?.id]);

  useEffect(() => {
    if (pdfData) {
      fetchPageImage(currentPage);
    }
  }, [currentPage, pdfData]);

  const fetchPageImage = async (pageNum) => {
    try {
      // Add timestamp to bust cache after saves
      const response = await axios.get(
        `${API}/documents/${document.id}/pdf-page-image/${pageNum}?t=${Date.now()}`,
        { headers: getAuthHeader() }
      );
      setPageImage(response.data);
    } catch (error) {
      toast.error("Failed to load page image");
    }
  };

  const handleCanvasClick = async (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = getScaleFactor();
    // Image is displayed at (pageImage.width/2)*scale, text coords are in PDF space (pageImage.width/2)
    const x = (e.clientX - rect.left) / scale;
    const y = (e.clientY - rect.top) / scale;

    if (activeTool === "text") {
      setNewTextPosition({ x, y });
      setAddTextDialog(true);
    } else if (activeTool === "comment") {
      setCommentPosition({ x, y });
      setCommentDialog(true);
    } else if (activeTool === "select") {
      // Check if clicked on a text block
      const pageData = pdfData?.pages?.find(p => p.page_num === currentPage);
      if (pageData) {
        let closestBlock = null;
        let minDistance = Infinity;
        const TOLERANCE = 15;
        
        for (const block of pageData.text_blocks) {
          const inX = x >= (block.x - TOLERANCE) && x <= (block.x + block.width + TOLERANCE);
          const inY = y >= (block.y - TOLERANCE) && y <= (block.y + block.height + TOLERANCE);
          
          if (inX && inY) {
            const centerX = block.x + block.width / 2;
            const centerY = block.y + block.height / 2;
            const distance = Math.sqrt(Math.pow(x - centerX, 2) + Math.pow(y - centerY, 2));
            
            if (distance < minDistance) {
              minDistance = distance;
              closestBlock = block;
            }
          }
        }
        
        if (closestBlock) {
          // Auto-save previous edit if text was changed, then select new block
          const pendingEdit = buildEditPayload();
          if (pendingEdit) {
            // Clear UI state immediately
            setEditingText(null);
            setSelectedText(null);
            setTextOffset({ x: 0, y: 0 });
            setDetectedFont(null);
            // Save in background
            saveEditToBackend(pendingEdit);
          }
          
          setSelectedText(closestBlock);
          setEditingText({ ...closestBlock, newText: closestBlock.text });
          setDetectedBgColor(null);
          setTextOffset({ x: 0, y: 0 });
          toast.info(`Selected: "${closestBlock.text.substring(0, 30)}${closestBlock.text.length > 30 ? '...' : ''}"`);
          
          // Detect background color for this text block
          detectBackgroundColor(closestBlock);
          return;
        }
      }
      // Clicked on empty area - auto-save if pending edit exists
      if (editingText && editingText.newText !== editingText.text) {
        await autoSaveCurrentEdit();
        return;
      }
      setSelectedText(null);
      setEditingText(null);
      setDetectedBgColor(null);
      setTextOffset({ x: 0, y: 0 });
    }
  };

  const detectBackgroundColor = async (block) => {
    setDetectingColor(true);
    try {
      // Detect background color
      const colorResponse = await axios.post(
        `${API}/documents/${document.id}/detect-background-color`,
        {
          page_num: currentPage,
          x: block.x,
          y: block.y,
          width: block.width,
          height: block.height
        },
        { headers: getAuthHeader() }
      );
      setDetectedBgColor(colorResponse.data);
      
      // Detect font properties
      const fontResponse = await axios.post(
        `${API}/documents/${document.id}/detect-font`,
        {
          page_num: currentPage,
          x: block.x,
          y: block.y,
          width: block.width,
          height: block.height
        },
        { headers: getAuthHeader() }
      );
      
      if (fontResponse.data.detected) {
        setDetectedFont(fontResponse.data);
        // Map detected PDF font name to our font family keys
        const name = (fontResponse.data.font_name || '').toLowerCase();
        let family = 'helv'; // default Helvetica
        if (name.includes('times') || name.includes('tiro') || name.includes('serif') || name.includes('roman')) {
          family = 'tiro';
        } else if (name.includes('courier') || name.includes('cour') || name.includes('mono')) {
          family = 'cour';
        } else if (fontResponse.data.is_bold) {
          family = 'hebo';
        } else if (fontResponse.data.is_italic) {
          family = 'heit';
        }

        // For OCR-detected blocks, additionally call suggest-font to pick the
        // closest serif/sans-serif from pixel analysis, since `font_name` from
        // detect-font is just "Helvetica-Bold" / "Helvetica" without family hint.
        try {
          if (fontResponse.data.source === 'ocr_pixel') {
            const sg = await axios.post(
              `${API}/documents/${document.id}/suggest-font`,
              { page_num: currentPage, x: block.x, y: block.y, width: block.width, height: block.height },
              { headers: getAuthHeader() }
            );
            const sugg = sg.data;
            setFontSuggestion(sugg);
            if (Array.isArray(sugg?.candidates) && sugg.candidates.length > 0) {
              family = sugg.candidates[0]; // top candidate
            }
          } else {
            setFontSuggestion(null);
          }
        } catch (_) {
          setFontSuggestion(null);
        }

        setFontSettings({
          size: fontResponse.data.font_size,
          family,
          bold: fontResponse.data.is_bold,
          italic: fontResponse.data.is_italic,
          color: fontResponse.data.color || '#000000',
          underline: false,
          strikethrough: false
        });
        console.log(`Detected font: ${fontResponse.data.font_name}, size=${fontResponse.data.font_size}, mapped family=${family}`);
      }
    } catch (error) {
      console.error("Error detecting properties:", error);
    } finally {
      setDetectingColor(false);
    }
  };

  const handleMouseDown = (e) => {
    if (["highlight", "underline", "strikethrough"].includes(activeTool)) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = getScaleFactor();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      setSelectionStart({ x, y });
      setSelectionRect(null);
    } else if (activeTool === "draw") {
      setIsDrawing(true);
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = getScaleFactor();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      setDrawingPoints([{ x, y }]);
    }
  };

  const handleMouseMove = (e) => {
    if (selectionStart && ["highlight", "underline", "strikethrough"].includes(activeTool)) {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = getScaleFactor();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      
      setSelectionRect({
        x: Math.min(selectionStart.x, x),
        y: Math.min(selectionStart.y, y),
        width: Math.abs(x - selectionStart.x),
        height: Math.abs(y - selectionStart.y)
      });
    } else if (isDrawing && activeTool === "draw") {
      const rect = canvasRef.current.getBoundingClientRect();
      const scale = getScaleFactor();
      const x = (e.clientX - rect.left) / scale;
      const y = (e.clientY - rect.top) / scale;
      setDrawingPoints(prev => [...prev, { x, y }]);
    }
  };

  const handleMouseUp = () => {
    if (selectionRect && ["highlight", "underline", "strikethrough"].includes(activeTool)) {
      const newAnnotation = {
        page_num: currentPage,
        type: activeTool,
        x: selectionRect.x,
        y: selectionRect.y,
        width: selectionRect.width,
        height: selectionRect.height,
        color: highlightColor
      };
      setAnnotations(prev => [...prev, newAnnotation]);
      setSelectionStart(null);
      setSelectionRect(null);
    } else if (isDrawing && drawingPoints.length > 1) {
      const newAnnotation = {
        page_num: currentPage,
        type: "drawing",
        x: drawingPoints[0].x,
        y: drawingPoints[0].y,
        color: highlightColor,
        points: drawingPoints
      };
      setAnnotations(prev => [...prev, newAnnotation]);
      setIsDrawing(false);
      setDrawingPoints([]);
    }
  };

  const handleAddText = () => {
    if (!newText.trim()) return;
    
    const addition = {
      page_num: currentPage,
      text: newText,
      x: newTextPosition.x,
      y: newTextPosition.y,
      font_size: newTextSize,
      color: highlightColor
    };
    
    setTextAdditions(prev => [...prev, addition]);
    setAddTextDialog(false);
    setNewText("");
    toast.success("Text added");
  };

  const handleAddComment = () => {
    if (!commentText.trim()) return;
    
    const annotation = {
      page_num: currentPage,
      type: "comment",
      x: commentPosition.x,
      y: commentPosition.y,
      text: commentText,
      color: highlightColor
    };
    
    setAnnotations(prev => [...prev, annotation]);
    setCommentDialog(false);
    setCommentText("");
    toast.success("Comment added");
  };

  // Core save function - saves an edit to the backend without clearing UI state
  const saveEditToBackend = async (edit) => {
    const allEdits = [...textEdits, edit];
    setSaving(true);
    try {
      await axios.post(
        `${API}/documents/${document.id}/pdf-edit-save`,
        { text_edits: allEdits, text_additions: textAdditions, highlights, annotations },
        { headers: getAuthHeader(), timeout: 30000 }
      );
      // Clear all pending edits since they're saved
      setTextEdits([]);
      setTextAdditions([]);
      setHighlights([]);
      setAnnotations([]);
      // Refresh page image
      await fetchPageImage(currentPage);
      setImageKey(prev => prev + 1);
      toast.success("Edit saved!", { duration: 1500 });
      return true;
    } catch (err) {
      console.error("Auto-save error:", err);
      toast.error("Save failed. Try again.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Build an edit payload from the current editing state
  const buildEditPayload = useCallback(() => {
    if (!editingText || editingText.newText === editingText.text) return null;
    return {
      page_num: currentPage,
      original_text: editingText.text,
      new_text: editingText.newText,
      x: editingText.x + textOffset.x,
      y: editingText.y + textOffset.y,
      width: editingText.width,
      height: editingText.height,
      is_ocr: editingText.is_ocr || false,
      font_size: fontSettings.size,
      font_family: fontSettings.family,
      is_bold: fontSettings.bold,
      is_italic: fontSettings.italic,
      font_color: fontSettings.color,
      underline: fontSettings.underline,
      strikethrough: fontSettings.strikethrough,
      // Pass detected background color so the backend redaction matches the
      // colour the user saw in the "Detected Background" swatch (was being
      // re-detected on the server, sometimes mis-picking dark ink → black marks).
      background_color: detectedBgColor || '#FFFFFF',
      _bg_color: detectedBgColor || '#FFFFFF',
      _font_css: fontSettings.family === 'tiro' ? '"Times New Roman", Times, serif' : 
                 fontSettings.family === 'cour' ? '"Courier New", Courier, monospace' : 
                 'Helvetica, Arial, sans-serif'
    };
  }, [editingText, currentPage, textOffset, fontSettings, detectedBgColor]);

  // Auto-focus the inline edit input whenever a new text block is selected for editing.
  // Ensures mobile users see the keyboard pop up immediately.
  useEffect(() => {
    if (editingText && inlineEditRef.current) {
      const el = inlineEditRef.current;
      requestAnimationFrame(() => {
        try {
          el.focus({ preventScroll: false });
          el.select?.();
        } catch (_) {}
      });
    }
  }, [editingText?.x, editingText?.y, editingText?.page_num]);

  // Auto-save: save the current pending edit and clear editing state
  const autoSaveCurrentEdit = async () => {
    const edit = buildEditPayload();
    if (!edit) return false;
    
    // Clear editing state immediately for responsive UI
    setEditingText(null);
    setSelectedText(null);
    setTextOffset({ x: 0, y: 0 });
    setDetectedFont(null);
    
    await saveEditToBackend(edit);
    return true;
  };

  // Legacy handler for the Apply Change button (kept for compatibility)
  const handleTextEdit = async () => {
    await autoSaveCurrentEdit();
  };

  // Reading-order navigation: find the next/previous text block on the current page.
  // Sort by (y, x) with a small tolerance for "same row" — so Tab goes left→right
  // within a row, then down to the next row. Shift+Tab goes the opposite way.
  const findAdjacentBlock = (currentBlock, direction = 1) => {
    const page = pdfData?.pages?.find(p => p.page_num === currentPage);
    const blocks = page?.text_blocks || [];
    if (blocks.length === 0 || !currentBlock) return null;
    const rowTolerance = Math.max(4, (currentBlock.height || 12) * 0.5);
    const sorted = [...blocks].sort((a, b) => {
      const dy = a.y - b.y;
      if (Math.abs(dy) <= rowTolerance) return a.x - b.x;
      return dy;
    });
    const idx = sorted.findIndex(b => (b.id && b.id === currentBlock.id) || (b.x === currentBlock.x && b.y === currentBlock.y));
    if (idx < 0) return null;
    const next = sorted[idx + direction];
    return next || null;
  };

  // Jump the inline editor to a different text block (used by Tab / Shift+Tab).
  // Auto-saves the current edit first (if changed) and then opens the next block for editing.
  const jumpToBlock = async (direction = 1) => {
    if (!editingText) return;
    const next = findAdjacentBlock(editingText, direction);
    // Save the current edit if it was actually modified
    if (editingText.newText !== editingText.text) {
      await autoSaveCurrentEdit();
    }
    if (next) {
      setSelectedText(next);
      setEditingText({ ...next, newText: next.text });
      setTextOffset({ x: 0, y: 0 });
      setDetectedBgColor(null);
      detectBackgroundColor(next);
    } else {
      setEditingText(null);
      toast.info(direction > 0 ? 'Reached last text on page' : 'Reached first text on page');
    }
  };

  // Auto-save when navigating pages
  const handlePageChange = async (newPage) => {
    if (newPage < 1 || newPage > (pdfData?.total_pages || 1) || newPage === currentPage) return;
    await autoSaveCurrentEdit();
    setCurrentPage(newPage);
  };

  // Auto-save when closing editor
  const handleClose = async () => {
    await autoSaveCurrentEdit();
    onClose();
  };

  const handleSave = async (saveToLibrary = false) => {
    // Auto-save current edit first
    await autoSaveCurrentEdit();
    setSaving(true);
    try {
      const payload = {
        text_edits: textEdits,
        text_additions: textAdditions,
        highlights: highlights,
        annotations: annotations,
        flatten_edits: flattenOnSave
      };
      console.log("Saving PDF with payload:", JSON.stringify(payload, null, 2));
      
      const response = await axios.post(
        `${API}/documents/${document.id}/pdf-edit-save`,
        payload,
        { headers: getAuthHeader() }
      );
      
      console.log("Save response:", response.data);
      
      // Clear edits after successful save
      setTextEdits([]);
      setTextAdditions([]);
      setHighlights([]);
      setAnnotations([]);
      
      // Refresh the page image to show changes
      await fetchPageImage(currentPage);
      setImageKey(prev => prev + 1); // Force image re-render
      
      toast.success("PDF saved successfully! Changes are now visible.");
      
      // Handle save to library
      if (saveToLibrary) {
        try {
          await axios.post(
            `${API}/documents/${document.id}/save-to-library`,
            { library_name: document.file_name, tags: [] },
            { headers: getAuthHeader() }
          );
          toast.success(`Saved '${document.file_name}' to Document Library!`);
        } catch (err) {
          toast.error("Failed to save to library");
        }
        onSave?.();
      } else {
        // Show dialog asking if user wants to save to library
        // Don't call onSave yet - wait for user's choice
        setSaveToLibraryDialog(true);
      }
    } catch (error) {
      console.error("Save error:", error);
      toast.error(error.response?.data?.detail || "Failed to save PDF");
    } finally {
      setSaving(false);
    }
  };

  const handleSaveToLibraryFromDialog = async () => {
    try {
      await axios.post(
        `${API}/documents/${document.id}/save-to-library`,
        { library_name: document.file_name, tags: [] },
        { headers: getAuthHeader() }
      );
      toast.success(`Saved '${document.file_name}' to Document Library!`);
    } catch (err) {
      toast.error("Failed to save to library");
    } finally {
      setSaveToLibraryDialog(false);
      onSave?.();
    }
  };

  const handleSkipLibrary = () => {
    setSaveToLibraryDialog(false);
    onSave?.();
  };

  // Open PDF in new tab for print preview - with pending changes applied
  const handlePrintPreview = async () => {
    try {
      // Auto-save current edit first
      await autoSaveCurrentEdit();
      
      let response;
      
      // Check if there are pending text edits (not yet auto-saved)
      if (textEdits.length > 0) {
        // Generate preview with pending changes
        toast.info("Generating preview with your changes...");
        response = await axios.post(
          `${API}/documents/${document.id}/pdf-edit-preview`,
          {
            text_edits: textEdits,
            text_additions: textAdditions,
            highlights: highlights,
            annotations: annotations
          },
          { 
            headers: getAuthHeader(),
            responseType: 'blob',
            timeout: 30000
          }
        );
        toast.success("Preview generated with pending changes!");
      } else {
        // All changes already saved - get the saved PDF
        response = await axios.get(
          `${API}/documents/${document.id}/download/converted`,
          { 
            headers: getAuthHeader(),
            responseType: 'blob',
            timeout: 30000
          }
        );
      }
      
      // Create blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      
      // Optional: Auto-trigger print dialog after PDF loads
      if (printWindow) {
        printWindow.onload = () => {
          setTimeout(() => {
            printWindow.print();
          }, 500);
        };
      }
    } catch (error) {
      console.error("Print preview error:", error);
      toast.error("Failed to open print preview");
    }
  };

  const undoLastAction = () => {
    if (annotations.length > 0) {
      setAnnotations(prev => prev.slice(0, -1));
    } else if (textAdditions.length > 0) {
      setTextAdditions(prev => prev.slice(0, -1));
    } else if (textEdits.length > 0) {
      setTextEdits(prev => prev.slice(0, -1));
    }
  };

  const getScaleFactor = () => zoom / 100;

  const tools = [
    { id: "select", icon: MousePointer, label: "Select" },
    { id: "text", icon: Type, label: "Add Text" },
    { id: "highlight", icon: Highlighter, label: "Highlight" },
    { id: "underline", icon: Underline, label: "Underline" },
    { id: "strikethrough", icon: Strikethrough, label: "Strikethrough" },
    { id: "comment", icon: MessageSquare, label: "Comment" },
    { id: "draw", icon: Pencil, label: "Draw" },
  ];

  const colors = [
    "#FFFF00", // Yellow
    "#00FF00", // Green
    "#FF69B4", // Pink
    "#87CEEB", // Light Blue
    "#FFA500", // Orange
    "#FF0000", // Red
    "#000000", // Black
  ];

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="p-8">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          <p className="mt-2 text-center">Loading PDF Editor...</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-slate-900 flex flex-col z-50">
      {/* Header Toolbar */}
      <div className="bg-white border-b px-2 sm:px-4 py-2 flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 sm:gap-4 min-w-0">
          <Button variant="ghost" size="sm" onClick={handleClose} className="px-2 sm:px-3">
            <X className="w-4 h-4 sm:mr-2" />
            <span className="hidden sm:inline">Close</span>
          </Button>
          <span className="font-medium text-sm sm:text-base truncate max-w-[120px] sm:max-w-xs">{document.file_name}</span>
          <span className="px-2 py-1 bg-emerald-100 text-emerald-800 text-xs font-medium rounded-full whitespace-nowrap hidden sm:inline-block">
            Text Editor
          </span>
          {onSwitchToCanvasEditor && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                onSwitchToCanvasEditor();
                toast.info('Switched to Image Editor — for scanned/image PDFs', { duration: 2200 });
              }}
              data-testid="switch-to-canvas-editor"
              className="border-blue-500 text-blue-700 hover:bg-blue-50 whitespace-nowrap"
              title="Switch to the Image Editor (canvas) — best for scanned/image PDFs"
            >
              <ImageIcon className="w-4 h-4 sm:mr-1.5" />
              <span className="hidden sm:inline text-xs">Use Image Editor</span>
            </Button>
          )}
          {pdfData?.is_ocr_document && (
            <span className="px-2 py-1 bg-amber-100 text-amber-800 text-xs font-medium rounded-full flex items-center gap-1 whitespace-nowrap">
              <span className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></span>
              OCR Mode
            </span>
          )}
          {saving && (
            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full flex items-center gap-1 whitespace-nowrap">
              <Loader2 className="w-3 h-3 animate-spin" />
              Saving...
            </span>
          )}
        </div>

        <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
          <Button variant="outline" size="sm" onClick={undoLastAction} className="px-2 sm:px-3">
            <Undo className="w-4 h-4" />
          </Button>

          <div className="flex items-center gap-1 sm:gap-2 border-l pl-2 sm:pl-4 ml-1 sm:ml-2">
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.max(50, zoom - 25))} className="px-2 sm:px-3">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm w-10 sm:w-12 text-center">{zoom}%</span>
            <Button variant="outline" size="sm" onClick={() => setZoom(Math.min(200, zoom + 25))} className="px-2 sm:px-3">
              <ZoomIn className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 border-l pl-2 sm:pl-4 ml-1 sm:ml-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-2 sm:px-3"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs sm:text-sm whitespace-nowrap">
              {currentPage}/{pdfData?.total_pages || 1}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= (pdfData?.total_pages || 1)}
              className="px-2 sm:px-3"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>

          <div className="flex items-center gap-1 sm:gap-2 ml-1 sm:ml-4">
            {/* Redaction-proof toggle: flattens edited pages to image so original text
                is unrecoverable. Hidden on mobile to save space. */}
            <label
              className="hidden sm:flex items-center gap-1.5 text-xs text-slate-700 px-2 py-1 rounded border border-slate-200 cursor-pointer hover:bg-slate-50"
              title="Rasterise edited pages so the original text cannot be extracted from the saved PDF"
              data-testid="flatten-toggle"
            >
              <input
                type="checkbox"
                checked={flattenOnSave}
                onChange={e => setFlattenOnSave(e.target.checked)}
                className="rounded text-red-600 focus:ring-red-500"
              />
              <span>{flattenOnSave ? '🔒 Redaction-proof' : 'Redaction-proof'}</span>
            </label>
            <Button
              onClick={() => handleSave(false)}
              disabled={saving}
              className={(flattenOnSave ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700') + ' px-2 sm:px-4 relative'}
              size="sm"
              data-testid="pdfeditor-save-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 sm:mr-2 animate-spin" /> : <Save className="w-4 h-4 sm:mr-2" />}
              <span className="hidden sm:inline">{flattenOnSave ? 'Save Sealed PDF' : 'Save PDF'}</span>
              {(() => {
                const pendingActive = editingText && editingText.newText !== editingText.text ? 1 : 0;
                const total = textEdits.length + textAdditions.length + highlights.length + annotations.length + pendingActive;
                return total > 0 ? (
                  <span className="ml-1 sm:ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 bg-white/95 text-green-700 text-[11px] font-bold rounded-full">
                    {total}
                  </span>
                ) : null;
              })()}
            </Button>
            {/* Undo-last quick chip — visible only when there's at least 1 pending edit */}
            {(() => {
              const pendingActive = editingText && editingText.newText !== editingText.text ? 1 : 0;
              const total = textEdits.length + textAdditions.length + highlights.length + annotations.length + pendingActive;
              return total > 0 ? (
                <button
                  onClick={undoLastAction}
                  className="ml-1 px-2 py-1 text-xs rounded-full border border-amber-400 bg-amber-50 text-amber-800 hover:bg-amber-100 active:scale-95 whitespace-nowrap"
                  data-testid="pdfeditor-undo-chip"
                  title="Undo most-recent edit"
                >
                  ↶ Undo
                </button>
              ) : null;
            })()}
            <Button
              onClick={() => handleSave(true)}
              disabled={saving}
              variant="outline"
              className="border-purple-600 text-purple-600 hover:bg-purple-50 px-2 sm:px-3 hidden sm:inline-flex"
              size="sm"
            >
              <FolderOpen className="w-4 h-4 mr-2" />
              Save to Library
            </Button>
            <Button
              onClick={handlePrintPreview}
              variant="outline"
              className="border-blue-600 text-blue-600 hover:bg-blue-50 px-2 sm:px-3 hidden sm:inline-flex"
              data-testid="print-preview-btn"
              size="sm"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Preview
            </Button>
          </div>
        </div>
      </div>

      {/* Tools Sidebar & Canvas */}
      <div className="flex flex-col sm:flex-row flex-1 overflow-hidden">
        {/* Tools Sidebar — vertical on desktop, horizontal scrolling strip on mobile */}
        <div className="w-full sm:w-16 bg-white border-b sm:border-b-0 sm:border-r flex sm:flex-col items-center py-2 sm:py-4 gap-1 sm:gap-2 overflow-x-auto sm:overflow-x-visible flex-shrink-0">
          {tools.map(tool => (
            <Button
              key={tool.id}
              variant={activeTool === tool.id ? "default" : "ghost"}
              size="sm"
              className={`w-10 h-10 sm:w-12 sm:h-12 p-0 flex-shrink-0 ${activeTool === tool.id ? 'bg-purple-600' : ''}`}
              onClick={() => setActiveTool(tool.id)}
              title={tool.label}
            >
              <tool.icon className="w-5 h-5" />
            </Button>
          ))}

          <div className="border-l sm:border-l-0 sm:border-t mx-1 sm:mx-0 sm:my-2 h-8 sm:h-auto sm:w-10"></div>

          {/* Color Picker */}
          <div className="flex sm:flex-col gap-1 flex-shrink-0">
            {colors.map(color => (
              <button
                key={color}
                className={`w-6 h-6 rounded border-2 flex-shrink-0 ${highlightColor === color ? 'border-purple-600' : 'border-gray-300'}`}
                style={{ backgroundColor: color }}
                onClick={() => setHighlightColor(color)}
                title={color}
              />
            ))}
          </div>
        </div>

        {/* Canvas Area */}
        <div 
          ref={containerRef}
          className="flex-1 overflow-auto bg-slate-700 p-8"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onTouchCancel={handleTouchEnd}
          style={{ touchAction: 'manipulation' }}
        >
          <div 
            className="relative mx-auto bg-white shadow-2xl"
            style={{
              width: pageImage ? (pageImage.width / 2) * getScaleFactor() : 600,
              height: pageImage ? (pageImage.height / 2) * getScaleFactor() : 800,
            }}
          >
            {/* PDF Page Image */}
            {pageImage && (
              <img
                ref={canvasRef}
                key={`page-${currentPage}-${imageKey}`}
                src={pageImage.image}
                alt={`Page ${currentPage}`}
                className="w-full h-full"
                onClick={handleCanvasClick}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                style={{ cursor: activeTool === "select" ? "text" : "crosshair" }}
              />
            )}

            {/* Selected text block highlight */}
            {selectedText && activeTool === 'select' && (
              <div
                className="absolute pointer-events-none ring-2 ring-purple-500 bg-purple-200/20 z-10"
                style={{
                  left: selectedText.x * getScaleFactor() - 2,
                  top: selectedText.y * getScaleFactor() - 2,
                  width: selectedText.width * getScaleFactor() + 4,
                  height: selectedText.height * getScaleFactor() + 4,
                }}
              />
            )}

            {/* Clickable text block overlays — visible on hover (desktop) or on tap (touch).
                On iOS the FIRST tap on an element with hover styles only triggers the
                hover state, NOT click. We bypass that by handling onTouchEnd directly
                and skipping the hover preview on touch devices. */}
            {activeTool === 'select' && pdfData?.pages?.find(p => p.page_num === currentPage)?.text_blocks?.map((block, idx) => {
              const blockKey = `${currentPage}-${block.id || idx}`;
              const cached = hoverPaperColors[blockKey];
              const isTouch = typeof window !== 'undefined' && ('ontouchstart' in window);
              const handleSelect = async (e) => {
                if (e && e.stopPropagation) e.stopPropagation();
                if (editingText && editingText.newText !== editingText.text) {
                  await autoSaveCurrentEdit();
                }
                setSelectedText(block);
                setEditingText({ ...block, newText: block.text });
                setDetectedBgColor(cached?.color || null);
                setTextOffset({ x: 0, y: 0 });
                toast.info(`Selected: "${block.text.substring(0, 30)}${block.text.length > 30 ? '...' : ''}"`);
                detectBackgroundColor(block);
              };
              return (
              <div
                key={`txtov-${idx}`}
                className="absolute cursor-text hover:bg-blue-200/30 hover:outline hover:outline-2 hover:outline-blue-400/60 transition-colors"
                style={{
                  left: block.x * getScaleFactor(),
                  top: block.y * getScaleFactor(),
                  width: block.width * getScaleFactor(),
                  height: block.height * getScaleFactor(),
                  zIndex: 5,
                  ...(cached && !isTouch ? { boxShadow: `inset 0 0 0 2px ${cached.color}80` } : {}),
                }}
                onMouseEnter={() => {
                  if (isTouch) return; // Skip hover preview on touch — iOS first-tap hover bug
                  setHoveredBlock(blockKey);
                  if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
                  if (hoverPaperColors[blockKey]) return;
                  hoverTimerRef.current = setTimeout(async () => {
                    try {
                      const res = await axios.post(
                        `${API}/documents/${document.id}/sample-edge-color`,
                        { page_num: currentPage, x: block.x, y: block.y, width: block.width, height: block.height },
                        { headers: getAuthHeader() }
                      );
                      if (res.data?.color) {
                        setHoverPaperColors(prev => ({ ...prev, [blockKey]: res.data }));
                      }
                    } catch (_) {}
                  }, 350);
                }}
                onMouseLeave={() => {
                  if (isTouch) return;
                  setHoveredBlock(null);
                  if (hoverTimerRef.current) { clearTimeout(hoverTimerRef.current); hoverTimerRef.current = null; }
                }}
                onClick={handleSelect}
                onTouchEnd={(e) => {
                  // Immediate selection on touch — bypass iOS first-tap-hover delay
                  e.preventDefault();
                  handleSelect(e);
                }}
                title={`Click to edit: "${block.text.substring(0, 40)}${block.text.length > 40 ? '...' : ''}"${cached ? `\nDetected paper colour: ${cached.color}` : ''}`}
              >
                {hoveredBlock === blockKey && cached && !isTouch && (
                  <div
                    className="absolute -top-6 left-0 bg-white border border-slate-300 rounded shadow px-1.5 py-0.5 flex items-center gap-1 text-[10px] font-mono whitespace-nowrap pointer-events-none"
                    style={{ zIndex: 20 }}
                  >
                    <span className="w-3 h-3 rounded border border-slate-400" style={{ backgroundColor: cached.color }}></span>
                    <span className="text-slate-700">{cached.color}</span>
                  </div>
                )}
              </div>
              );
            })}

            {/* Selection Rectangle Overlay */}
            {selectionRect && (
              <div
                className="absolute pointer-events-none"
                style={{
                  left: selectionRect.x * getScaleFactor(),
                  top: selectionRect.y * getScaleFactor(),
                  width: selectionRect.width * getScaleFactor(),
                  height: selectionRect.height * getScaleFactor(),
                  backgroundColor: `${highlightColor}40`,
                  border: `2px dashed ${highlightColor}`
                }}
              />
            )}

            {/* Render annotations preview */}
            {annotations.filter(a => a.page_num === currentPage).map((annot, idx) => (
              <div
                key={idx}
                className="absolute pointer-events-none"
                style={{
                  left: annot.x * getScaleFactor(),
                  top: annot.y * getScaleFactor(),
                  width: (annot.width || 20) * getScaleFactor(),
                  height: (annot.height || 20) * getScaleFactor(),
                  backgroundColor: annot.type === "highlight" ? `${annot.color}60` : 
                                  annot.type === "comment" ? "transparent" : undefined,
                  borderBottom: annot.type === "underline" ? `3px solid ${annot.color}` : undefined,
                  textDecoration: annot.type === "strikethrough" ? `line-through ${annot.color}` : undefined,
                }}
              >
                {annot.type === "comment" && (
                  <MessageSquare className="w-5 h-5" style={{ color: annot.color }} />
                )}
              </div>
            ))}

            {/* LIVE WYSIWYG editable input — type directly on top of the original PDF text.
                This was previously a non-interactive preview; making it an actual <input>
                eliminates the confusion of having to find a hidden sidebar text-area on mobile. */}
            {editingText && editingText.newText !== undefined && (
              <div
                className="absolute"
                data-testid="live-wysiwyg-preview"
                style={{
                  left: (editingText.x + textOffset.x) * getScaleFactor(),
                  top: (editingText.y + textOffset.y) * getScaleFactor(),
                  minWidth: Math.max(120, editingText.width * getScaleFactor() + 40),
                  height: editingText.height * getScaleFactor(),
                  zIndex: 25,
                }}
              >
                {/* Background to cover original text */}
                <div
                  className="absolute pointer-events-none"
                  style={{
                    left: -1,
                    top: -1,
                    right: -1,
                    bottom: -1,
                    backgroundColor: detectedBgColor || '#FFFFFF',
                  }}
                />
                {/* Inline editable input — autofocused so user can just start typing */}
                <input
                  ref={inlineEditRef}
                  type="text"
                  data-testid="inline-edit-input"
                  value={editingText.newText}
                  onChange={(e) => setEditingText({ ...editingText, newText: e.target.value })}
                  onKeyDown={async (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      await autoSaveCurrentEdit();
                      setEditingText(null);
                    } else if (e.key === 'Escape') {
                      e.preventDefault();
                      setEditingText(null);
                    } else if (e.key === 'Tab') {
                      e.preventDefault();
                      await jumpToBlock(e.shiftKey ? -1 : 1);
                    }
                  }}
                  className="absolute inset-0 w-full h-full bg-transparent outline-none px-1"
                  style={{
                    fontSize: `${Math.max(12, editingText.height * getScaleFactor() * 0.85)}px`,
                    fontFamily: fontSettings.family === 'tiro' ? '"Times New Roman", Times, serif' :
                               fontSettings.family === 'cour' ? '"Courier New", Courier, monospace' :
                               'Helvetica, Arial, sans-serif',
                    fontWeight: fontSettings.bold ? 'bold' : 'normal',
                    fontStyle: fontSettings.italic ? 'italic' : 'normal',
                    color: fontSettings.color || '#000000',
                    textDecoration: [
                      fontSettings.underline && 'underline',
                      fontSettings.strikethrough && 'line-through'
                    ].filter(Boolean).join(' ') || 'none',
                    lineHeight: 1.1,
                  }}
                />
                {/* Blue dashed border to show active edit area */}
                <div
                  className="absolute border-2 border-blue-500 border-dashed pointer-events-none"
                  style={{
                    left: -2,
                    top: -2,
                    right: -2,
                    bottom: -2,
                  }}
                />
                {/* Floating mini-toolbar — gives mobile users an obvious "Done" action */}
                <div
                  className="absolute -top-9 left-0 flex items-center gap-1 bg-white rounded-md shadow-md border border-slate-200 px-1.5 py-1 text-xs whitespace-nowrap"
                  style={{ zIndex: 26 }}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={(e) => e.stopPropagation()}
                >
                  <button
                    type="button"
                    className="px-1.5 py-0.5 rounded text-slate-600 hover:bg-slate-100"
                    data-testid="inline-edit-prev"
                    title="Previous text (Shift+Tab)"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await jumpToBlock(-1);
                    }}
                  >‹ Prev</button>
                  <button
                    type="button"
                    className="px-1.5 py-0.5 rounded text-slate-600 hover:bg-slate-100"
                    data-testid="inline-edit-next"
                    title="Next text (Tab)"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await jumpToBlock(1);
                    }}
                  >Next ›</button>
                  <span className="text-slate-300">|</span>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded bg-green-600 text-white font-medium hover:bg-green-700"
                    data-testid="inline-edit-done"
                    onClick={async (e) => {
                      e.stopPropagation();
                      await autoSaveCurrentEdit();
                      setEditingText(null);
                    }}
                  >
                    ✓ Done
                  </button>
                  <button
                    type="button"
                    className="px-2 py-0.5 rounded text-slate-600 hover:bg-slate-100"
                    data-testid="inline-edit-cancel"
                    onClick={(e) => {
                      e.stopPropagation();
                      setEditingText(null);
                    }}
                  >
                    ✕ Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Render TEXT EDITS preview - show new text replacing original */}
            {textEdits.filter(e => e.page_num === currentPage).map((edit, idx) => (
              <div
                key={`edit-${idx}`}
                className="absolute"
                style={{
                  left: edit.x * getScaleFactor(),
                  top: edit.y * getScaleFactor(),
                  minWidth: edit.width * getScaleFactor(),
                  height: edit.height * getScaleFactor(),
                  zIndex: 10,
                }}
              >
                {/* Background matching detected page color to cover original text */}
                <div 
                  className="absolute"
                  style={{ 
                    left: -2,
                    top: -2,
                    right: -2,
                    bottom: -2,
                    backgroundColor: edit._bg_color || '#FFFFFF',
                  }}
                />
                {/* New text overlay with matching font */}
                <div 
                  className="absolute inset-0 flex items-center overflow-visible"
                  style={{ 
                    fontSize: `${edit.height * getScaleFactor() * 0.85}px`,
                    fontFamily: edit._font_css || 'Helvetica, Arial, sans-serif',
                    fontWeight: edit.is_bold ? 'bold' : 'normal',
                    fontStyle: edit.is_italic ? 'italic' : 'normal',
                    color: edit.font_color || '#000000',
                    textDecoration: [
                      edit.underline && 'underline',
                      edit.strikethrough && 'line-through'
                    ].filter(Boolean).join(' ') || 'none',
                    lineHeight: 1.1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {edit.new_text || '(deleted)'}
                </div>
                {/* Green dashed border to indicate edited area */}
                <div 
                  className="absolute border-2 border-green-500 border-dashed pointer-events-none"
                  style={{
                    left: -2,
                    top: -2,
                    right: -2,
                    bottom: -2,
                  }}
                />
              </div>
            ))}

            {/* Render text additions preview */}
            {textAdditions.filter(a => a.page_num === currentPage).map((addition, idx) => (
              <div
                key={`add-${idx}`}
                className="absolute pointer-events-none font-sans"
                style={{
                  left: addition.x * getScaleFactor(),
                  top: addition.y * getScaleFactor(),
                  fontSize: addition.font_size * getScaleFactor(),
                  color: addition.color
                }}
              >
                {addition.text}
              </div>
            ))}

            {/* Drawing preview */}
            {isDrawing && drawingPoints.length > 1 && (
              <svg
                className="absolute inset-0 pointer-events-none"
                style={{ width: '100%', height: '100%' }}
              >
                <polyline
                  points={drawingPoints.map(p => `${p.x * getScaleFactor()},${p.y * getScaleFactor()}`).join(' ')}
                  fill="none"
                  stroke={highlightColor}
                  strokeWidth="2"
                />
              </svg>
            )}

            {/* Selected text indicator */}
            {selectedText && (
              <div
                className="absolute border-2 border-purple-500 bg-purple-100/30"
                style={{
                  left: selectedText.x * getScaleFactor(),
                  top: selectedText.y * getScaleFactor(),
                  width: selectedText.width * getScaleFactor(),
                  height: selectedText.height * getScaleFactor(),
                }}
              />
            )}
          </div>
        </div>

        {/* Right Panel - Properties — sidebar on desktop, conditionally-shown bottom sheet on mobile.
            On mobile we hide it by default (so canvas takes full height) and only show
            it when a block has been selected for editing OR when user taps the floating
            "?" FAB. This guarantees the input field is visible right after tapping a word. */}
        <div className={`${(editingText || showMobilePropsPanel) ? 'block' : 'hidden'} sm:block w-full sm:w-72 max-h-[55vh] sm:max-h-none bg-white border-t sm:border-t-0 sm:border-l p-3 sm:p-4 overflow-y-auto flex-shrink-0`}>
          <div className="flex items-center justify-between mb-2 sm:hidden">
            <h3 className="font-semibold text-sm">Edit Text</h3>
            <button
              onClick={() => { setShowMobilePropsPanel(false); }}
              className="p-1 rounded hover:bg-slate-100"
              data-testid="props-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <h3 className="font-semibold mb-4">Properties</h3>
          
          {editingText && (
            <div className="space-y-4">
              <div>
                <Label>Original Text</Label>
                <p className="text-sm text-slate-500 bg-slate-50 p-2 rounded mt-1">
                  {editingText.text}
                </p>
              </div>
              
              {/* Background Color Indicator */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <Label className="text-xs text-slate-600 mb-2 block">Detected Background</Label>
                {detectingColor ? (
                  <div className="flex items-center gap-2 text-sm text-slate-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span>Detecting color...</span>
                  </div>
                ) : detectedBgColor ? (
                  <div className="flex items-center gap-3">
                    <div 
                      className="w-8 h-8 rounded border-2 border-slate-300 shadow-inner"
                      style={{ backgroundColor: detectedBgColor.hex }}
                      title={`RGB(${detectedBgColor.r}, ${detectedBgColor.g}, ${detectedBgColor.b})`}
                    />
                    <div className="text-xs">
                      <p className="font-mono font-medium">{detectedBgColor.hex}</p>
                      <p className="text-slate-500">
                        {detectedBgColor.is_colored ? (
                          <span className="text-green-600 flex items-center gap-1">
                            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
                            Color will be preserved
                          </span>
                        ) : (
                          <span className="text-slate-400">White background</span>
                        )}
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-slate-400">Select text to detect</p>
                )}
              </div>
              
              <div>
                <Label>New Text <span className="text-xs text-slate-400">(Preview in detected font)</span></Label>
                <Textarea
                  data-testid="wysiwyg-textarea"
                  value={editingText.newText}
                  onChange={(e) => setEditingText({ ...editingText, newText: e.target.value })}
                  className="mt-1 transition-all"
                  rows={3}
                  style={{
                    fontFamily: fontSettings.family === 'tiro' ? '"Times New Roman", Times, serif' : 
                               fontSettings.family === 'cour' ? '"Courier New", Courier, monospace' : 
                               'Helvetica, Arial, sans-serif',
                    fontSize: `${Math.min(fontSettings.size, 24)}px`,
                    fontWeight: fontSettings.bold ? 'bold' : 'normal',
                    fontStyle: fontSettings.italic ? 'italic' : 'normal',
                    color: fontSettings.color,
                    textDecoration: [
                      fontSettings.underline && 'underline',
                      fontSettings.strikethrough && 'line-through'
                    ].filter(Boolean).join(' ') || 'none',
                    lineHeight: '1.4'
                  }}
                />
                <p className="text-xs text-slate-400 mt-1" data-testid="font-preview-label">
                  {fontSettings.family === 'tiro' ? 'Times Roman' :
                   fontSettings.family === 'cour' ? 'Courier' :
                   (fontSettings.bold ? 'Helvetica Bold' : 
                    fontSettings.italic ? 'Helvetica Italic' : 'Helvetica')}, {fontSettings.size}pt
                  {fontSettings.bold && ', Bold'}{fontSettings.italic && ', Italic'}
                </p>
              </div>
              
              {/* Position Adjustment Controls */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <Label className="text-xs text-slate-600 mb-2 block">Position Adjustment</Label>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-slate-500">Horizontal (←→)</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0"
                        onClick={() => setTextOffset(prev => ({ ...prev, x: prev.x - 1 }))}
                      >
                        ←
                      </Button>
                      <span className="text-xs font-mono w-8 text-center">{textOffset.x}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0"
                        onClick={() => setTextOffset(prev => ({ ...prev, x: prev.x + 1 }))}
                      >
                        →
                      </Button>
                    </div>
                  </div>
                  <div>
                    <Label className="text-xs text-slate-500">Vertical (↑↓)</Label>
                    <div className="flex items-center gap-1 mt-1">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0"
                        onClick={() => setTextOffset(prev => ({ ...prev, y: prev.y - 1 }))}
                      >
                        ↑
                      </Button>
                      <span className="text-xs font-mono w-8 text-center">{textOffset.y}</span>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="h-7 w-7 p-0"
                        onClick={() => setTextOffset(prev => ({ ...prev, y: prev.y + 1 }))}
                      >
                        ↓
                      </Button>
                    </div>
                  </div>
                </div>
                {(textOffset.x !== 0 || textOffset.y !== 0) && (
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="w-full mt-2 text-xs h-6"
                    onClick={() => setTextOffset({ x: 0, y: 0 })}
                  >
                    Reset Position
                  </Button>
                )}
                <p className="text-xs text-slate-400 mt-2">
                  Adjust to align text with other cells
                </p>
              </div>
              
              {/* Font Formatting Controls */}
              <div className="border rounded-lg p-3 bg-slate-50">
                <Label className="text-xs text-slate-600 mb-2 block">Font Formatting</Label>
                
                {/* Detected Font Info */}
                {detectedFont && detectedFont.detected && (
                  <div className="text-xs text-green-600 mb-3 p-2 bg-green-50 rounded border border-green-200">
                    <div className="font-medium mb-1">Detected from PDF:</div>
                    <div className="font-mono">{detectedFont.font_name}</div>
                    <div>Size: {detectedFont.font_size}pt</div>
                    {(detectedFont.is_bold || detectedFont.is_italic) && (
                      <div>Style: {detectedFont.is_bold && 'Bold'}{detectedFont.is_bold && detectedFont.is_italic && ' + '}{detectedFont.is_italic && 'Italic'}</div>
                    )}
                    <div className="flex items-center gap-1">
                      Color: <span className="font-mono">{detectedFont.color}</span>
                      <span 
                        className="inline-block w-3 h-3 rounded-sm border border-slate-300"
                        style={{ backgroundColor: detectedFont.color }}
                      />
                    </div>
                  </div>
                )}
                
                {/* Font Size */}
                <div className="mb-3">
                  <Label className="text-xs text-slate-500">Size (pt)</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 w-7 p-0"
                      onClick={() => setFontSettings(prev => ({ ...prev, size: Math.max(6, prev.size - 1) }))}
                    >
                      -
                    </Button>
                    <input 
                      type="number" 
                      value={fontSettings.size}
                      onChange={(e) => setFontSettings(prev => ({ ...prev, size: Math.max(6, parseInt(e.target.value) || 12) }))}
                      className="text-sm font-mono w-14 text-center border rounded px-1 py-1"
                      min="6"
                      max="72"
                    />
                    <Button 
                      size="sm" 
                      variant="outline" 
                      className="h-7 w-7 p-0"
                      onClick={() => setFontSettings(prev => ({ ...prev, size: prev.size + 1 }))}
                    >
                      +
                    </Button>
                  </div>
                </div>
                
                {/* Font Family — full catalogue dropdown + AI suggestion badge */}
                <div className="mb-3">
                  <div className="flex items-center justify-between">
                    <Label className="text-xs text-slate-500">Font Family</Label>
                    {fontSuggestion && fontSuggestion.confidence !== 'low' && (
                      <span
                        className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-semibold"
                        title={`Detected family: ${fontSuggestion.family} (${fontSuggestion.confidence} confidence)`}
                      >
                        AI: {fontSuggestion.family}
                      </span>
                    )}
                  </div>
                  <select
                    value={fontSettings.family}
                    onChange={(e) => setFontSettings(prev => ({ ...prev, family: e.target.value }))}
                    className="mt-1 w-full text-sm border rounded-md px-2 py-1.5 bg-white focus:ring-2 focus:ring-purple-500"
                    data-testid="font-family-select"
                  >
                    {(availableFonts.length > 0 ? availableFonts : [
                      { key: 'helv', label: 'Helvetica', family: 'sans' },
                      { key: 'tiro', label: 'Times Roman', family: 'serif' },
                      { key: 'cour', label: 'Courier', family: 'mono' },
                    ]).map(f => (
                      <option key={f.key} value={f.key}>
                        {f.label}{fontSuggestion?.candidates?.includes(f.key) ? ' ✦' : ''}
                      </option>
                    ))}
                  </select>
                  {fontSuggestion?.candidates && fontSuggestion.candidates.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-1.5">
                      {fontSuggestion.candidates.slice(0, 3).map(ck => {
                        const f = availableFonts.find(af => af.key === ck);
                        if (!f) return null;
                        return (
                          <button
                            key={ck}
                            onClick={() => setFontSettings(prev => ({ ...prev, family: ck }))}
                            className={`text-[10px] px-2 py-0.5 rounded-full border ${
                              fontSettings.family === ck
                                ? 'bg-purple-600 text-white border-purple-600'
                                : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                            }`}
                            data-testid={`font-suggestion-${ck}`}
                            title={`AI suggestion (${fontSuggestion.confidence})`}
                          >
                            ✦ {f.label.split(' ')[0]}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
                
                {/* Bold/Italic/Underline/Strikethrough */}
                <div className="mb-3">
                  <Label className="text-xs text-slate-500">Style</Label>
                  <div className="flex gap-2 mt-1">
                    <Button
                      size="sm"
                      variant={fontSettings.bold ? 'default' : 'outline'}
                      className="h-7 px-3 font-bold"
                      onClick={() => setFontSettings(prev => ({ 
                        ...prev, 
                        bold: !prev.bold,
                        family: !prev.bold ? 'hebo' : 'helv'
                      }))}
                    >
                      B
                    </Button>
                    <Button
                      size="sm"
                      variant={fontSettings.italic ? 'default' : 'outline'}
                      className="h-7 px-3 italic"
                      onClick={() => setFontSettings(prev => ({ 
                        ...prev, 
                        italic: !prev.italic,
                        family: !prev.italic ? 'heit' : 'helv'
                      }))}
                    >
                      I
                    </Button>
                    <Button
                      size="sm"
                      data-testid="font-underline-btn"
                      variant={fontSettings.underline ? 'default' : 'outline'}
                      className="h-7 px-3 underline"
                      onClick={() => setFontSettings(prev => ({ ...prev, underline: !prev.underline }))}
                    >
                      U
                    </Button>
                    <Button
                      size="sm"
                      data-testid="font-strikethrough-btn"
                      variant={fontSettings.strikethrough ? 'default' : 'outline'}
                      className="h-7 px-3 line-through"
                      onClick={() => setFontSettings(prev => ({ ...prev, strikethrough: !prev.strikethrough }))}
                    >
                      S
                    </Button>
                  </div>
                </div>
                
                {/* Font Color */}
                <div className="mb-2">
                  <Label className="text-xs text-slate-500">Text Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="relative">
                      <input
                        type="color"
                        data-testid="font-color-picker"
                        value={fontSettings.color}
                        onChange={(e) => setFontSettings(prev => ({ ...prev, color: e.target.value }))}
                        className="w-8 h-7 rounded border border-slate-300 cursor-pointer p-0"
                      />
                    </div>
                    <span className="text-xs font-mono text-slate-500" data-testid="font-color-value">{fontSettings.color}</span>
                    {fontSettings.color !== '#000000' && (
                      <Button 
                        size="sm" 
                        variant="ghost" 
                        className="h-6 px-2 text-xs"
                        onClick={() => setFontSettings(prev => ({ ...prev, color: '#000000' }))}
                      >
                        Reset
                      </Button>
                    )}
                  </div>
                  {detectedFont?.color && detectedFont.color !== '#000000' && (
                    <p className="text-xs text-blue-600 mt-1">
                      Original: <span className="font-mono">{detectedFont.color}</span>
                      <span 
                        className="inline-block w-3 h-3 rounded-sm ml-1 border border-slate-300 align-middle"
                        style={{ backgroundColor: detectedFont.color }}
                      />
                    </p>
                  )}
                </div>
              </div>
              
              {/* Auto-save indicator */}
              <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 p-2 rounded">
                {saving ? (
                  <>
                    <Loader2 className="h-3 w-3 animate-spin text-purple-600" />
                    <span>Saving...</span>
                  </>
                ) : (
                  <>
                    <Save className="h-3 w-3 text-green-600" />
                    <span>Auto-save enabled. Click elsewhere or select another text to save.</span>
                  </>
                )}
              </div>
            </div>
          )}

          {!editingText && (
            <div className="space-y-4">
              <div className="text-sm text-slate-500">
                <p className="font-medium mb-2">How to use:</p>
                <ul className="list-disc list-inside space-y-1">
                  <li><strong>Select</strong>: Click text to edit</li>
                  <li><strong>Add Text</strong>: Click to add new text</li>
                  <li><strong>Highlight</strong>: Drag to highlight area</li>
                  <li><strong>Comment</strong>: Click to add sticky note</li>
                  <li><strong>Draw</strong>: Freehand drawing</li>
                </ul>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm font-medium mb-2">Pending Changes:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li>Text Edits: {textEdits.length}</li>
                  <li>Text Additions: {textAdditions.length}</li>
                  <li>Annotations: {annotations.length}</li>
                </ul>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add Text Dialog */}
      <Dialog open={addTextDialog} onOpenChange={setAddTextDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Text</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Text</Label>
              <Textarea
                value={newText}
                onChange={(e) => setNewText(e.target.value)}
                placeholder="Enter text..."
                rows={3}
              />
            </div>
            <div>
              <Label>Font Size: {newTextSize}px</Label>
              <Slider
                value={[newTextSize]}
                onValueChange={([val]) => setNewTextSize(val)}
                min={8}
                max={48}
                step={1}
                className="mt-2"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddTextDialog(false)}>Cancel</Button>
            <Button onClick={handleAddText} className="bg-purple-600 hover:bg-purple-700">Add Text</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comment Dialog */}
      <Dialog open={commentDialog} onOpenChange={setCommentDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Comment</DialogTitle>
          </DialogHeader>
          <div>
            <Label>Comment</Label>
            <Textarea
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder="Enter your comment..."
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCommentDialog(false)}>Cancel</Button>
            <Button onClick={handleAddComment} className="bg-purple-600 hover:bg-purple-700">Add Comment</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save to Library Dialog */}
      <Dialog open={saveToLibraryDialog} onOpenChange={setSaveToLibraryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Save to Document Library?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Your PDF has been saved. Would you like to also save it to your Document Library for easy access later?
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleSkipLibrary}>No, Thanks</Button>
            <Button onClick={handleSaveToLibraryFromDialog} className="bg-purple-600 hover:bg-purple-700">
              <FolderOpen className="w-4 h-4 mr-2" />
              Yes, Save to Library
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Floating "?" FAB on mobile — opens Properties panel manually */}
      {!showMobilePropsPanel && !editingText && (
        <button
          onClick={() => setShowMobilePropsPanel(true)}
          className="sm:hidden fixed bottom-4 right-4 z-50 w-12 h-12 rounded-full bg-purple-600 text-white shadow-lg flex items-center justify-center text-xl font-bold active:scale-95"
          data-testid="pdfeditor-help-fab"
          aria-label="Show editor info"
        >
          ?
        </button>
      )}
    </div>
  );
};

export default PDFEditor;
